import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from 'assets/icons/pencil.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { formatTextTitle } from 'utils/formatText';
import { shortBigNumber } from 'utils/helpers';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  onEditItem,
  locationsCountries,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Analysis table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }) => (
              <TableCell head className={styles.tableCell} key={id}>
                <div>
                  <span className={styles.tableCellLabel}>
                    {id === 'actions' ? '' : t(label)}
                  </span>
                </div>
              </TableCell>
            ))}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>{item?.stakeholder?.name || item?.vcmStakeholder?.name || hyphen}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {item.stockTickers.join(', ') || hyphen}
                </TableCell>
                <TableCell>
                  {locationCountry(item.countryCode) || hyphen}
                </TableCell>
                <TableCell>
                  {item.employeeCount || hyphen}
                </TableCell>
                <TableCell>
                  {item?.stakeholder?.type ? formatTextTitle(item?.stakeholder?.type) : null}
                  {item?.vcmStakeholder?.type ? formatTextTitle(item?.vcmStakeholder?.type) : null}
                  {!item?.stakeholder?.type && !item?.vcmStakeholder?.type ? hyphen : null}
                </TableCell>
                <TableCell>
                  {shortBigNumber({ actual: item.supplierExpenses }) || hyphen}
                </TableCell>
                <TableCell>
                  {shortBigNumber({ actual: item.customerPurchases }) || hyphen}
                </TableCell>
                <TableCell>
                  {shortBigNumber({ actual: item.revenue }) || hyphen}
                </TableCell>
                <TableCell>
                  <IconButton
                    icon={<EditIcon />}
                    onClick={() => onEditItem(item.id)}
                  />
                </TableCell>
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
